/* eslint-disable @typescript-eslint/ban-ts-comment */
import { v4 as uuidv4 } from 'uuid'; // npm install uuid
import React, { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';

interface Message<D> {
  type: string;
  data: D;
}

interface MessageRequest<D> extends Message<D> {
  requestId: string;
}

interface MessageResponse<D> {
  requestId: string;
  data: D;
}

interface IncomingMessage<D> {
  type: string
  channel: string
  timestamp: number
  data: D

}

interface WebSocketContextType {
  sendMessage: <D = unknown>(msg: Message<D>) => void;
  sendRequest: <D = unknown, R = unknown>(msg: Message<D>) => Promise<MessageResponse<R>>;
  subscribe: <D = unknown>(channel: string, callback: (data: IncomingMessage<D>) => void) => () => void;
  isConnected: boolean;
}

interface WebSocketProviderProps {
  url: string;
  children: ReactNode;
  headers?: Record<string, string>;
}

const WebSocketContext = createContext<WebSocketContextType | null>(null);

export const WebSocketProvider = ({ url, children, headers }: WebSocketProviderProps) => {
  const wsRef = useRef<WebSocket>();
  const [isConnected, setIsConnected] = useState(false);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const reconnectTimeout = useRef<NodeJS.Timeout | null>(null);

  const listenersRef = useRef<Record<string, ((data: any) => void)[]>>({});
  const pendingRequests = useRef<
    Record<
      string,
      {
        resolve: <R>(value: MessageResponse<R>) => void;
        reject: (reason: any) => void;
      }
    >
  >({});

  const [pendingQueue, setPendingQueue] = useState<any[]>([]);

  const connect = () => {
    const queryString = headers ? '?' + new URLSearchParams(headers).toString() : '';
    const fullUrl = url + queryString;
    const ws = new WebSocket(fullUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setIsConnected(true);
      setReconnectAttempts(0);
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
        reconnectTimeout.current = null;
      }
      // Enviar mensajes pendientes
      setPendingQueue((queue) => {
        queue.forEach((msg) => ws.send(JSON.stringify(msg)));
        return [];
      });
      // Notificar suscriptores de conexión
      if (listenersRef.current['connected']) {
        listenersRef.current['connected'].forEach((cb) => cb(undefined));
      }
      console.log('[WS] Conectado');
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        const { channel, requestId } = data;
        console.log('WSS: evento: ' + channel, data);
        if (requestId && pendingRequests.current[requestId]) {
          pendingRequests.current[requestId].resolve(data);
          delete pendingRequests.current[requestId];
          return;
        }
        if (channel && listenersRef.current[channel]) {
          listenersRef.current[channel].forEach((cb) => cb(data));
        }
      } catch (error) {
        console.error('[WS] Error parsing message:', error);
      }
    };

    ws.onclose = () => {
      setIsConnected(false);
      Object.values(pendingRequests.current).forEach(({ reject }) => reject(new Error('WebSocket connection closed')));
      pendingRequests.current = {};
      console.warn('[WS] Desconectado');
      // Notificar suscriptores de desconexión
      if (listenersRef.current['disconnected']) {
        listenersRef.current['disconnected'].forEach((cb) => cb(undefined));
      }
      // Reconexión automática
      const nextAttempt = reconnectAttempts + 1;
      setReconnectAttempts(nextAttempt);
      const timeout = Math.min(1000 * 2 ** nextAttempt, 30000); // backoff exponencial, máx 30s
      reconnectTimeout.current = setTimeout(() => {
        connect();
      }, timeout);
    };

    ws.onerror = (error) => {
      console.error('[WS] Error:', error);
    };
  };

  useEffect(() => {
    connect();
    return () => {
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
      }
      Object.values(pendingRequests.current).forEach(({ reject }) => reject(new Error('Component unmounted')));
      wsRef.current?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, JSON.stringify(headers)]);

  const sendMessage = (msg: Message<unknown>) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    } else {
      setPendingQueue((queue) => [...queue, msg]);
      console.warn('[WS] No se puede enviar, WebSocket cerrado. Mensaje encolado.');
    }
  };
  const subscribe: <D = unknown>(
    channel: string,
    callback: (data: IncomingMessage<D>) => void
  ) => () => void = (channel, callback) => {
    if (!listenersRef.current[channel]) {
      listenersRef.current[channel] = [];
    }
    listenersRef.current[channel].push(callback);
    console.log('WSS: suscrito: ' + channel);
    sendMessage({ type: 'subscribe', data: { channel } });
    return () => {
      if (listenersRef.current[channel]) {
        sendMessage({ type: 'unsubscribe', data: { channel } });
        listenersRef.current[channel] = listenersRef.current[channel].filter((cb) => cb !== callback);
      }
    };
  };

  const sendRequest: <D = unknown, R = unknown>(message: Message<D>) => Promise<MessageResponse<R>> = (message) => {
    const requestId = uuidv4();
    const requestMsg = { ...message, requestId } as MessageRequest<unknown>;
    return new Promise((resolve, reject) => {
      if (wsRef.current?.readyState !== WebSocket.OPEN) {
        reject(new Error('WebSocket not connected'));
        return;
      }
      // @ts-ignore
      pendingRequests.current[requestId] = { resolve, reject };
      wsRef.current.send(JSON.stringify(requestMsg));
      setTimeout(() => {
        if (pendingRequests.current[requestId]) {
          reject(new Error('Timeout de respuesta'));
          delete pendingRequests.current[requestId];
        }
      }, 50000);
    });
  };

  return (
    <WebSocketContext.Provider
      value={{
        sendMessage,
        sendRequest,
        subscribe,
        isConnected
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};
